import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
  TaxRuleService,
  Provider,
  OperationType,
  Category,
  RoundingMode,
  TaxRule,
  TaxSimulateDTO
} from 'src/app/service/tax-rule.service';

@Component({
  selector: 'app-tax-rules-admin',
  templateUrl: './tax-rules-admin.component.html',
  styleUrls: ['./tax-rules-admin.component.css'],
  standalone: false
})
export class TaxRulesAdminComponent implements OnInit {

  rules: TaxRule[] = [];
  loading = false;

  providers: Provider[] = ['ALL', 'SPAY'];
  operations: OperationType[] = ['DEPOSIT', 'WITHDRAW', 'TRANSFER', 'MERCHANT_PAY', 'BILL_PAY'];
  categories: Category[] = ['STANDARD', 'GAMBLING'];
  roundings: RoundingMode[] = ['CEIL', 'FLOOR', 'HALF_UP'];

  selected?: TaxRule;

  form = this.fb.group({
    countryCode: ['CM', [Validators.required]],
    provider: ['ALL' as Provider, [Validators.required]],
    operationType: ['TRANSFER' as OperationType, [Validators.required]],
    category: ['STANDARD' as Category, [Validators.required]],
    rateBps: [20, [Validators.required, Validators.min(0)]],
    fixedAmount: [4, [Validators.required, Validators.min(0)]],
    currency: ['XAF', [Validators.required]],
    minAmount: [null],
    maxAmount: [null],
    roundingMode: ['CEIL' as RoundingMode, [Validators.required]],
    effectiveFrom: [new Date().toISOString().slice(0, 16), [Validators.required]],
    effectiveTo: [null],
    isActive: [true],
    priority: [0]
  });

  simForm = this.fb.group({
    countryCode: ['CM', [Validators.required]],
    provider: ['ALL' as Provider, [Validators.required]],
    operationType: ['TRANSFER' as OperationType, [Validators.required]],
    category: ['STANDARD' as Category, [Validators.required]],
    amount: [10000, [Validators.required, Validators.min(0)]],
    currency: ['XAF', [Validators.required]],
  });

  simResult: any;

  constructor(private api: TaxRuleService, private fb: FormBuilder) { }

  ngOnInit() { this.refresh(); }

  refresh() {
    this.loading = true;
    this.api.list().subscribe({
      next: data => { this.rules = data; this.loading = false; },
      error: _ => { this.loading = false; }
    });
  }

  newRule() {
    this.selected = undefined;
    this.form.reset({
      countryCode: 'CM', provider: 'ALL', operationType: 'TRANSFER', category: 'STANDARD',
      rateBps: 20, fixedAmount: 4, currency: 'XAF',
      roundingMode: 'CEIL', effectiveFrom: new Date().toISOString().slice(0, 16),
      isActive: true, priority: 0
    });
  }

  editRule(r: TaxRule) {
    this.selected = r;
    this.form.patchValue({
      countryCode: r.countryCode,
      provider: r.provider,
      operationType: r.operationType,
      category: r.category,
      rateBps: r.rateBps,
      fixedAmount: r.fixedAmount,
      currency: r.currency,
      minAmount: r.minAmount ?? null,
      maxAmount: r.maxAmount ?? null,
      roundingMode: r.roundingMode,
      effectiveFrom: (r.effectiveFrom || '').slice(0, 16),
      effectiveTo: r.effectiveTo ? r.effectiveTo.slice(0, 16) : null,
      isActive: (r.isActive ?? (r as any).active ?? true),
      priority: r.priority ?? 0
    });
  }

  save() {
    if (this.form.invalid) return;

    const payload: any = {
      ...this.form.value,
      effectiveFrom: this.form.value.effectiveFrom ? new Date(this.form.value.effectiveFrom).toISOString() : null,
      effectiveTo: this.form.value.effectiveTo ? new Date(this.form.value.effectiveTo).toISOString() : null
    };
    payload.active = payload.isActive;

    if (!this.selected) {
      this.api.create(payload).subscribe(_ => this.refresh());
    } else {
      this.api.update(this.selected.id, { ...payload, changeNote: 'admin update' }).subscribe(_ => this.refresh());
    }
  }

  deactivate(r: TaxRule) {
    this.api.deactivate(r.id, 'admin deactivated').subscribe(_ => this.refresh());
  }

  simulate() {
    if (this.simForm.invalid) return;
    const payload = this.simForm.getRawValue() as unknown as TaxSimulateDTO;
    this.api.simulate(payload).subscribe(res => this.simResult = res);
  }
}
